import { LinkDevicePeripheralOptionsResponse } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import { PeripheralType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { TFunction } from 'i18next';
import { assertUnreachable } from '~/types/assert-type';

type PeripheralToLink = {
  peripheralType: PeripheralType;
  peripheralName: string;
  productTitle: string;
};

type PeripheralGroup = {
  type: PeripheralType;
  peripherals: PeripheralToLink[];
};

export class LinkDevicePeripheralOptionsModel {
  private readonly serialsByPeripheral: Map<string, string[]>;

  readonly groups: PeripheralGroup[];

  constructor(response: LinkDevicePeripheralOptionsResponse) {
    this.serialsByPeripheral = new Map();
    const groupedByType = new Map<PeripheralType, PeripheralToLink[]>();

    for (const option of response.peripheralOptions) {
      this.serialsByPeripheral.set(
        option.peripheralName,
        option.peripheralSerial,
      );

      const peripheral: PeripheralToLink = {
        peripheralType: option.type,
        peripheralName: option.peripheralName,
        productTitle: option.productTitle,
      };

      const existing = groupedByType.get(option.type) ?? [];
      groupedByType.set(option.type, [...existing, peripheral]);
    }

    this.groups = Array.from(groupedByType.entries()).map(
      ([type, peripherals]) => ({
        type,
        peripherals,
      }),
    );
  }

  getAvailableSerials(
    peripheralName: string,
    selectedSerials: Record<string, string>,
  ): string[] {
    const allSerials = this.serialsByPeripheral.get(peripheralName) ?? [];

    const otherSelectedSerials = new Set(
      Object.entries(selectedSerials)
        .filter(([key, value]) => key !== peripheralName && value)
        .map(([, value]) => value),
    );

    return allSerials.filter((serial) => !otherSelectedSerials.has(serial));
  }

  static getPeripheralsGroupLabel(type: PeripheralType, t: TFunction): string {
    switch (type) {
      case PeripheralType.FLOW_SENSOR:
        return t('system.commissioning.peripheralLinking.type.flowSensor');
      case PeripheralType.RAIN_SENSOR:
        return t('system.commissioning.peripheralLinking.type.rainSensor');
      case PeripheralType.HUMIDITY_SENSOR:
        return t('system.commissioning.peripheralLinking.type.humiditySensor');
      case PeripheralType.PUMP_ACTUATOR:
        return t('system.commissioning.peripheralLinking.type.pumpActuator');
      case PeripheralType.TAP_ACTUATOR:
        return t('system.commissioning.peripheralLinking.type.tapActuator');
      case PeripheralType.TEMPERATURE_SENSOR:
        return t(
          'system.commissioning.peripheralLinking.type.temperatureSensor',
        );
      case PeripheralType.VALVE_ACTUATOR:
        return t('system.commissioning.peripheralLinking.type.valveActuator');
      case PeripheralType.WATER_HEIGHT_SENSOR:
        return t(
          'system.commissioning.peripheralLinking.type.waterHeightSensor',
        );
      case PeripheralType.WIND_SENSOR:
        return t('system.commissioning.peripheralLinking.type.windSensor');
      case PeripheralType.UNSPECIFIED:
        throw new Error('Peripheral type is unspecified');
      default:
        return assertUnreachable(type);
    }
  }
}
